document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.hc-product-page').forEach((page) => {
    const slides = [...page.querySelectorAll('.hc-gallery__slide')];
    const thumbs = [...page.querySelectorAll('[data-gallery-thumb]')];
    const thumbsContainer = page.querySelector('.hc-gallery__thumbs');

    let activeIndex = Math.max(0, slides.findIndex((element) => element.classList.contains('is-active')));

    /**
     * Keeps the active thumbnail visible without moving the document vertically.
     * Native scrollIntoView() was removed because it can scroll both the thumbnail
     * strip and the entire page when a variant image changes.
     *
     * @param {HTMLElement | undefined} thumbnail
     * @param {ScrollBehavior} behavior
     */
    const keepThumbnailVisible = (thumbnail, behavior = 'smooth') => {
      if (!thumbnail || !(thumbsContainer instanceof HTMLElement)) return;

      const containerRect = thumbsContainer.getBoundingClientRect();
      const thumbnailRect = thumbnail.getBoundingClientRect();
      const currentLeft = thumbsContainer.scrollLeft;
      let targetLeft = currentLeft;

      if (thumbnailRect.left < containerRect.left) {
        targetLeft -= containerRect.left - thumbnailRect.left;
      } else if (thumbnailRect.right > containerRect.right) {
        targetLeft += thumbnailRect.right - containerRect.right;
      }

      if (targetLeft !== currentLeft) {
        thumbsContainer.scrollTo({
          left: Math.max(0, targetLeft),
          top: thumbsContainer.scrollTop,
          behavior,
        });
      }
    };

    /**
     * Displays a gallery slide while preserving the page's current vertical position.
     *
     * @param {number} index
     * @param {{ preservePagePosition?: boolean, thumbnailBehavior?: ScrollBehavior }} [options]
     */
    const showSlide = (index, options = {}) => {
      if (!slides.length) return;

      const { preservePagePosition = false, thumbnailBehavior = 'smooth' } = options;
      const savedScrollX = window.scrollX;
      const savedScrollY = window.scrollY;

      activeIndex = (index + slides.length) % slides.length;

      slides.forEach((element, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        element.classList.toggle('is-active', isActive);
        element.setAttribute('aria-hidden', String(!isActive));
      });

      thumbs.forEach((element, thumbIndex) => {
        const isActive = thumbIndex === activeIndex;
        element.classList.toggle('is-active', isActive);
        element.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      keepThumbnailVisible(thumbs[activeIndex], thumbnailBehavior);

      if (preservePagePosition) {
        // Restore twice because image/layout updates may complete on the next frame.
        requestAnimationFrame(() => {
          window.scrollTo({ left: savedScrollX, top: savedScrollY, behavior: 'auto' });
          requestAnimationFrame(() => {
            window.scrollTo({ left: savedScrollX, top: savedScrollY, behavior: 'auto' });
          });
        });
      }
    };

    page.querySelector('[data-gallery-prev]')?.addEventListener('click', () => {
      showSlide(activeIndex - 1);
    });

    page.querySelector('[data-gallery-next]')?.addEventListener('click', () => {
      showSlide(activeIndex + 1);
    });

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => showSlide(index));
    });

    const productForm = page.querySelector('.hc-product-form');
    const personalization = page.querySelector('[data-hc-personalization]');
    const personalizationError = page.querySelector('[data-hc-personalization-error]');

    const personalizationValid = () =>
      Boolean(personalization && personalization.value.trim().length >= 2);

    const renderPersonalizationState = (showError = false) => {
      if (!personalization) return true;

      const valid = personalizationValid();
      personalization.classList.toggle('is-invalid', showError && !valid);

      if (personalizationError) {
        personalizationError.hidden = !(showError && !valid);
      }

      return valid;
    };

    personalization?.addEventListener('input', () => renderPersonalizationState(false));
    personalization?.addEventListener('blur', () => renderPersonalizationState(true));

    productForm?.addEventListener(
      'submit',
      (event) => {
        if (renderPersonalizationState(true)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        personalization.focus({ preventScroll: true });
        personalization.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      true
    );

    const variantsElement = page.querySelector('[data-product-variants]');
    if (!variantsElement) return;

    let variants = [];

    try {
      variants = JSON.parse(variantsElement.textContent || '[]');
    } catch (error) {
      console.error('Unable to parse product variants.', error);
      return;
    }

    const optionGroups = [...page.querySelectorAll('[data-option-index]')];
    const idInput = page.querySelector('[data-variant-id]');
    const priceElement = page.querySelector('[data-hc-price]');
    const comparePriceElement = page.querySelector('[data-hc-compare]');
    const addButton = page.querySelector('[data-hc-add]');
    const addText = page.querySelector('[data-hc-add-text]');

    const formatMoney = (cents) =>
      new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);

    const updateVariant = () => {
      const savedScrollX = window.scrollX;
      const savedScrollY = window.scrollY;
      const values = optionGroups.map(
        (group) => group.querySelector('.hc-option-button.is-selected')?.dataset.optionValue
      );
      const variant = variants.find((candidate) =>
        values.every((value, optionIndex) => candidate.options[optionIndex] === value)
      );

      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addText) addText.textContent = 'Unavailable';
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceElement) priceElement.textContent = formatMoney(variant.price);

      if (comparePriceElement) {
        const hasComparePrice = Number(variant.compare_at_price) > Number(variant.price);
        comparePriceElement.hidden = !hasComparePrice;
        comparePriceElement.textContent = hasComparePrice ? formatMoney(variant.compare_at_price) : '';
      }

      if (addButton) addButton.disabled = !variant.available;
      if (addText) addText.textContent = variant.available ? 'Add to cart' : 'Sold out';

      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('variant', variant.id);
      history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);

      if (variant.featured_media) {
        const mediaIndex = slides.findIndex(
          (element) => Number(element.dataset.mediaId) === Number(variant.featured_media.id)
        );

        if (mediaIndex >= 0) {
          showSlide(mediaIndex, {
            preservePagePosition: true,
            thumbnailBehavior: 'auto',
          });
        }
      }

      // Guard against any app/theme callback trying to reposition the page after the option changes.
      requestAnimationFrame(() => {
        window.scrollTo({ left: savedScrollX, top: savedScrollY, behavior: 'auto' });
        requestAnimationFrame(() => {
          window.scrollTo({ left: savedScrollX, top: savedScrollY, behavior: 'auto' });
        });
      });
    };

    optionGroups.forEach((group) => {
      group.querySelectorAll('.hc-option-button').forEach((button) => {
        button.addEventListener('click', () => {
          group.querySelectorAll('.hc-option-button').forEach((element) => {
            element.classList.remove('is-selected');
            element.setAttribute('aria-pressed', 'false');
          });

          button.classList.add('is-selected');
          button.setAttribute('aria-pressed', 'true');
          updateVariant();
        });
      });
    });
  });
});
